import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    // Default to light mode
    return saved === 'dark';
  });
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Load user preference from backend when logged in
  const loadUserPreference = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/preferences`, { withCredentials: true });
      if (response.data?.theme) {
        const serverTheme = response.data.theme === 'dark';
        setIsDark(serverTheme);
        localStorage.setItem('theme', response.data.theme);
        setIsLoggedIn(true);
      }
    } catch {
      // Not logged in or error - use localStorage
      setIsLoggedIn(false);
    }
  }, []);

  // Save preference to backend when logged in
  const saveUserPreference = useCallback(async (theme) => {
    if (!isLoggedIn) return;
    try {
      await axios.put(`${API}/user/preferences`, { theme }, { withCredentials: true });
    } catch {
      // Silently fail - localStorage still works
    }
  }, [isLoggedIn]);

  // Load preference on mount
  useEffect(() => {
    loadUserPreference();
  }, [loadUserPreference]);

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.add('light');
    }
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const toggleTheme = useCallback(() => {
    setIsDark(prev => {
      const newTheme = !prev;
      saveUserPreference(newTheme ? 'dark' : 'light');
      return newTheme;
    });
  }, [saveUserPreference]);

  // Function to refresh preferences (call after login)
  const refreshPreferences = useCallback(() => {
    loadUserPreference();
  }, [loadUserPreference]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme, refreshPreferences }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
